import { Link } from 'react-router-dom'
import { CalendarDays } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth-context'
import { usePermissions } from '@/hooks/usePermissions'
import { PERMISSIONS } from '@/lib/permissions'

interface OpenInPlanningLinkProps {
  projectId: string
  /**
   * Whether the planning cockpit knows this project (a phase-1 métré + pose exist). Derived by the
   * page from `selectPlanningSectionState` — no extra API call.
   */
  isPlanningInitialized: boolean
}

/**
 * SOLY-NAV-001 — the door from the project file to this project's planning.
 *
 * It lives in the breadcrumb band, which is the only row on the page whose job is « where am I /
 * where can I go », and which already carries the Client → Chantier → Projet axis the trade thinks
 * in (DESIGN-CONTEXT §2.4). The metadata cluster below it (commercial, langue, Modifier, kebab) is
 * a different grammar: editing this project, not leaving it. The band is also sticky, which matters
 * on a page this long — the semantically "right" home would be the Exécution section header, but
 * that sits at the very bottom, and a link you have to scroll to find is a link that isn't there.
 *
 * It mirrors the door the drawer already offers back this way (« Chantier ↗ »), so the two surfaces
 * have one entrance each.
 *
 * Same tab, deliberately. The drawer opens the project file in a NEW tab because the cockpit is a
 * working context you don't want to lose; the project file is a reference surface and the browser's
 * back button returns to it. A cockpit open twice is heavy and drifts out of sync.
 *
 * All THREE conditions of the target route are checked here, not two: the route
 * `/planning/projects/:id` is `ProtectedRoute feature="planning" permission={PLANNING_READ}`, and
 * relying on the caller's state to imply the org flag left half the guard in the caller. Hidden,
 * not disabled — it points at a whole page, and `permissions.ts` greys a gesture but hides a page.
 *
 * `variant="outline"` for the focus ring and the house geometry, but with an EXPLICIT
 * `border-slate-500`. Measured in the running app: `variant="outline"` paints `bg-background`,
 * which on this sticky band is byte-identical to the band itself (`rgb(248,250,252)`), and the
 * default `--color-border` sits at **1.18:1** against it — the same number as the bare border it
 * replaced. Only a 5%-alpha shadow separated the two. `slate-400` still measures 2.45:1 here;
 * `slate-500` reaches 4.55:1 and clears WCAG 1.4.11's 3:1 for a UI component boundary. This screen
 * is driven on tablets, where hover does not exist, so the boundary is the whole affordance.
 *
 * The underweight `--color-border` against `--color-background` is systemic, not local to this
 * button — every outline control on a `bg-background` surface has it. Fixed here because this lot
 * introduces the control; the token itself is a separate change.
 */
export function OpenInPlanningLink({ projectId, isPlanningInitialized }: OpenInPlanningLinkProps) {
  const { user } = useAuth()
  const { can } = usePermissions()

  if (!user?.features?.planning) return null
  if (!isPlanningInitialized || !can(PERMISSIONS.PLANNING_READ)) return null

  return (
    <Button asChild variant="outline" size="sm" className="shrink-0 gap-1.5 border-slate-500 pointer-coarse:min-h-11">
      <Link
        to={`/planning/projects/${projectId}`}
        data-testid="project-open-in-planning"
        aria-label="Voir dans le planning"
        title="Voir dans le planning"
      >
        <CalendarDays className="h-4 w-4" aria-hidden />
        {/* The label folds away on the narrowest screens; aria-label above carries the name. */}
        <span className="hidden sm:inline">Voir dans le planning</span>
      </Link>
    </Button>
  )
}
